import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useParticipation,
  useRackMutations,
  useRacks,
  useStallMutations,
  useStalls,
} from '../api';
import { downloadRackQr } from '../api';
import { useScope } from '../../store/scope';
import { PageLoader, ErrorState, EmptyState, StatusBadge } from '../../components/ui';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import { dateRange, num } from '../../lib/format';
import type { Rack, Stall } from '../../lib/types';

const STATUSES = ['active', 'inactive'] as const;
type EntityStatus = (typeof STATUSES)[number];

const emptyStall = {
  stallNumber: '',
  stallName: '',
  hall: '',
  zone: '',
  location: '',
  description: '',
  status: 'active' as EntityStatus,
};
const emptyRack = {
  rackNumber: '',
  rackName: '',
  description: '',
  status: 'active' as EntityStatus,
  generateQr: true,
};

type StallForm = typeof emptyStall;
type RackForm = typeof emptyRack;

export function ParticipationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const setScope = useScope((s) => s.set);

  const participation = useParticipation(id);
  const stalls = useStalls(id);
  const [stallId, setStallId] = useState('');
  const racks = useRacks(stallId || undefined);

  const stallMut = useStallMutations(id);
  const rackMut = useRackMutations(stallId || undefined);

  const [stallModal, setStallModal] = useState<null | { mode: 'create' | 'edit'; row?: Stall }>(null);
  const [stallForm, setStallForm] = useState<StallForm>(emptyStall);
  const [rackModal, setRackModal] = useState<null | { mode: 'create' | 'edit'; row?: Rack }>(null);
  const [rackForm, setRackForm] = useState<RackForm>(emptyRack);

  const [delStall, setDelStall] = useState<null | { row: Stall; force: boolean }>(null);
  const [delRack, setDelRack] = useState<null | { row: Rack; force: boolean }>(null);
  const [regenRack, setRegenRack] = useState<Rack | null>(null);
  const [qrBusyId, setQrBusyId] = useState('');

  const stallList = useMemo(() => stalls.data?.data ?? [], [stalls.data]);
  const rackList = useMemo(() => racks.data?.data ?? [], [racks.data]);
  const selectedStall = stallList.find((s) => s._id === stallId) ?? null;

  // Keep a stall selected once the list loads.
  useEffect(() => {
    if (!stallId && stallList.length) setStallId(stallList[0]._id);
    if (stallId && stallList.length && !stallList.some((s) => s._id === stallId)) {
      setStallId(stallList[0]?._id ?? '');
    }
  }, [stallList, stallId]);

  const refreshStats = () => {
    qc.invalidateQueries({ queryKey: ['participation', id] });
    qc.invalidateQueries({ queryKey: ['participations'] });
    qc.invalidateQueries({ queryKey: ['stalls', id] });
  };

  // ── Stall CRUD ──
  const openStallCreate = () => {
    setStallForm(emptyStall);
    setStallModal({ mode: 'create' });
  };
  const openStallEdit = (row: Stall) => {
    setStallForm({
      stallNumber: row.stallNumber ?? '',
      stallName: row.stallName ?? '',
      hall: row.hall ?? '',
      zone: row.zone ?? '',
      location: row.location ?? '',
      description: row.description ?? '',
      status: row.status,
    });
    setStallModal({ mode: 'edit', row });
  };
  const saveStall = async () => {
    const body = trimmed(stallForm);
    if (!body.stallNumber) return toast('Stall number is required', 'error');
    try {
      if (stallModal?.mode === 'create') {
        const created = await stallMut.create.mutateAsync(body);
        setStallId(created._id);
        toast('Stall added', 'success');
      } else if (stallModal?.row) {
        await stallMut.update.mutateAsync({ id: stallModal.row._id, body });
        toast('Stall updated', 'success');
      }
      setStallModal(null);
      refreshStats();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Save failed', 'error');
    }
  };
  const confirmDeleteStall = async () => {
    if (!delStall) return;
    try {
      await stallMut.remove.mutateAsync({ id: delStall.row._id, force: delStall.force });
      toast('Stall deleted', 'success');
      if (stallId === delStall.row._id) setStallId('');
      setDelStall(null);
      refreshStats();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === 'HAS_CHILDREN') {
        setDelStall({ row: delStall.row, force: true });
        return;
      }
      toast(e instanceof ApiRequestError ? e.message : 'Delete failed', 'error');
      setDelStall(null);
    }
  };

  // ── Rack CRUD ──
  const openRackCreate = () => {
    setRackForm(emptyRack);
    setRackModal({ mode: 'create' });
  };
  const openRackEdit = (row: Rack) => {
    setRackForm({
      rackNumber: row.rackNumber ?? '',
      rackName: row.rackName ?? '',
      description: row.description ?? '',
      status: row.status,
      generateQr: true,
    });
    setRackModal({ mode: 'edit', row });
  };
  const saveRack = async () => {
    if (!stallId) return;
    const body = trimmed(rackForm);
    if (!body.rackNumber) return toast('Rack number is required', 'error');
    try {
      if (rackModal?.mode === 'create') {
        await rackMut.create.mutateAsync(body);
        toast('Rack added', 'success');
      } else if (rackModal?.row) {
        const { generateQr, ...rest } = body;
        await rackMut.update.mutateAsync({ id: rackModal.row._id, body: rest });
        toast('Rack updated', 'success');
      }
      setRackModal(null);
      refreshStats();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Save failed', 'error');
    }
  };
  const confirmDeleteRack = async () => {
    if (!delRack) return;
    try {
      await rackMut.remove.mutateAsync({ id: delRack.row._id, force: delRack.force });
      toast('Rack deleted', 'success');
      setDelRack(null);
      refreshStats();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === 'HAS_CHILDREN') {
        setDelRack({ row: delRack.row, force: true });
        return;
      }
      toast(e instanceof ApiRequestError ? e.message : 'Delete failed', 'error');
      setDelRack(null);
    }
  };
  const regenerate = async () => {
    if (!regenRack) return;
    try {
      await rackMut.generateQr.mutateAsync({ id: regenRack._id, rotateToken: true });
      toast('QR regenerated — the old code no longer works', 'success');
      setRegenRack(null);
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Failed', 'error');
    }
  };
  const downloadQr = async (rack: Rack) => {
    setQrBusyId(rack._id);
    try {
      await downloadRackQr(rack._id, rack.rackNumber);
    } catch {
      toast('QR download failed', 'error');
    } finally {
      setQrBusyId('');
    }
  };

  if (participation.isLoading) return <PageLoader />;
  if (participation.error)
    return (
      <ErrorState
        message={(participation.error as Error).message}
        onRetry={() => participation.refetch()}
      />
    );

  const p = participation.data;
  const stats = p?.stats;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/participations" className="text-xs text-ink-400 hover:underline">
            ← All participations
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="font-serif text-2xl font-semibold text-ink-900">{p?.expoName}</h1>
            {p && <StatusBadge status={p.status} />}
          </div>
          <p className="mt-0.5 text-sm text-ink-400">
            {p?.organizerName}
            {p?.city ? ` · ${p.city}` : ''}
            {p ? ` · ${dateRange(p.startDate, p.endDate)}` : ''}
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            if (id) setScope(id);
            navigate('/admin/assignments');
          }}
        >
          Assign products →
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:max-w-md">
        <MiniStat label="Stalls" value={num(stats?.stallCount ?? stallList.length)} />
        <MiniStat label="Racks" value={num(stats?.rackCount ?? 0)} />
        <MiniStat label="Products" value={num(stats?.assignedProductCount ?? 0)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        {/* Stall rail */}
        <div className="card h-fit p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="label">Stalls</p>
            <button className="btn-ghost px-2 py-1 text-xs" onClick={openStallCreate}>
              + Add
            </button>
          </div>
          {stalls.isLoading ? (
            <PageLoader label="…" />
          ) : stallList.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-ink-400">
              No stalls yet. Add the first one to start placing racks.
            </p>
          ) : (
            <div className="space-y-1">
              {stallList.map((s) => (
                <button
                  key={s._id}
                  onClick={() => setStallId(s._id)}
                  className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ${
                    stallId === s._id ? 'bg-ink-900 text-white' : 'hover:bg-ink-100'
                  }`}
                >
                  <span className="min-w-0 truncate">
                    {s.stallNumber}
                    {s.stallName ? (
                      <span className={stallId === s._id ? 'text-ink-300' : 'text-ink-400'}>
                        {' '}
                        · {s.stallName}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={`ml-2 shrink-0 text-xs ${
                      stallId === s._id ? 'text-ink-300' : 'text-ink-400'
                    }`}
                  >
                    {s.rackCount} rk{s.status === 'inactive' ? ' · off' : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Racks for the selected stall */}
        <div className="space-y-3">
          {!selectedStall ? (
            <EmptyState
              title="Pick a stall"
              hint="Select a stall on the left (or add one) to manage its racks and QR codes."
            />
          ) : (
            <>
              <div className="card flex flex-wrap items-center justify-between gap-2 p-3">
                <div>
                  <p className="font-medium text-ink-800">
                    {selectedStall.stallNumber}
                    {selectedStall.stallName ? ` — ${selectedStall.stallName}` : ''}
                  </p>
                  <p className="text-xs text-ink-400">
                    {[selectedStall.hall && `Hall ${selectedStall.hall}`, selectedStall.zone && `Zone ${selectedStall.zone}`, selectedStall.location]
                      .filter(Boolean)
                      .join(' · ') || 'No location details'}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button className="btn-ghost px-2 py-1 text-sm" onClick={() => openStallEdit(selectedStall)}>
                    Edit stall
                  </button>
                  <button
                    className="btn-ghost px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => setDelStall({ row: selectedStall, force: false })}
                  >
                    Delete
                  </button>
                  <button className="btn-primary px-3 py-1 text-sm" onClick={openRackCreate}>
                    + Add rack
                  </button>
                </div>
              </div>

              {racks.isLoading ? (
                <PageLoader />
              ) : racks.error ? (
                <ErrorState message={(racks.error as Error).message} onRetry={() => racks.refetch()} />
              ) : rackList.length === 0 ? (
                <EmptyState
                  title="No racks in this stall yet"
                  hint="Each rack gets its own QR code that visitors scan to browse the jewellery on it."
                  action={
                    <button className="btn-primary" onClick={openRackCreate}>
                      Add the first rack
                    </button>
                  }
                />
              ) : (
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="table-head">
                          <th className="px-3 py-2">Rack</th>
                          <th className="px-3 py-2">QR link</th>
                          <th className="px-3 py-2 text-right">Products</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ink-50">
                        {rackList.map((r) => (
                          <tr key={r._id} className="hover:bg-ink-50/60">
                            <td className="px-3 py-2">
                              <div className="font-medium">{r.rackNumber}</div>
                              {r.rackName && <div className="text-xs text-ink-400">{r.rackName}</div>}
                            </td>
                            <td className="px-3 py-2">
                              {r.qrUrl ? (
                                <a
                                  href={r.qrUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-mono text-xs text-gold-600 hover:underline"
                                >
                                  /r/{r.qrToken}
                                </a>
                              ) : (
                                <span className="font-mono text-xs text-ink-300">{r.qrToken}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">{num(r.assignedProductCount)}</td>
                            <td className="px-3 py-2">
                              <StatusBadge status={r.status} />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex justify-end gap-1">
                                <button
                                  className="btn-ghost px-2 py-1"
                                  disabled={qrBusyId === r._id}
                                  onClick={() => downloadQr(r)}
                                >
                                  {qrBusyId === r._id ? '…' : 'QR'}
                                </button>
                                <button className="btn-ghost px-2 py-1" onClick={() => setRegenRack(r)}>
                                  Regenerate
                                </button>
                                <button className="btn-ghost px-2 py-1" onClick={() => openRackEdit(r)}>
                                  Edit
                                </button>
                                <button
                                  className="btn-ghost px-2 py-1 text-red-600 hover:bg-red-50"
                                  onClick={() => setDelRack({ row: r, force: false })}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stall modal */}
      <Modal
        open={!!stallModal}
        onClose={() => setStallModal(null)}
        title={stallModal?.mode === 'create' ? 'Add stall' : 'Edit stall'}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setStallModal(null)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={stallMut.create.isPending || stallMut.update.isPending}
              onClick={saveStall}
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stall number *">
            <input
              className="input"
              value={stallForm.stallNumber}
              onChange={(e) => setStallForm({ ...stallForm, stallNumber: e.target.value })}
              placeholder="e.g. A-12"
            />
          </Field>
          <Field label="Stall name">
            <input
              className="input"
              value={stallForm.stallName}
              onChange={(e) => setStallForm({ ...stallForm, stallName: e.target.value })}
            />
          </Field>
          <Field label="Hall">
            <input
              className="input"
              value={stallForm.hall}
              onChange={(e) => setStallForm({ ...stallForm, hall: e.target.value })}
            />
          </Field>
          <Field label="Zone">
            <input
              className="input"
              value={stallForm.zone}
              onChange={(e) => setStallForm({ ...stallForm, zone: e.target.value })}
            />
          </Field>
          <Field label="Location" span>
            <input
              className="input"
              value={stallForm.location}
              onChange={(e) => setStallForm({ ...stallForm, location: e.target.value })}
              placeholder="Free-text position on the floor plan"
            />
          </Field>
          <Field label="Status">
            <select
              className="input"
              value={stallForm.status}
              onChange={(e) => setStallForm({ ...stallForm, status: e.target.value as EntityStatus })}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Description" span>
            <textarea
              className="input min-h-[60px]"
              value={stallForm.description}
              onChange={(e) => setStallForm({ ...stallForm, description: e.target.value })}
            />
          </Field>
        </div>
      </Modal>

      {/* Rack modal */}
      <Modal
        open={!!rackModal}
        onClose={() => setRackModal(null)}
        title={rackModal?.mode === 'create' ? `Add rack to ${selectedStall?.stallNumber ?? ''}` : 'Edit rack'}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setRackModal(null)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={rackMut.create.isPending || rackMut.update.isPending}
              onClick={saveRack}
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rack number *">
            <input
              className="input"
              value={rackForm.rackNumber}
              onChange={(e) => setRackForm({ ...rackForm, rackNumber: e.target.value })}
              placeholder="e.g. R1"
            />
          </Field>
          <Field label="Rack name">
            <input
              className="input"
              value={rackForm.rackName}
              onChange={(e) => setRackForm({ ...rackForm, rackName: e.target.value })}
            />
          </Field>
          <Field label="Status">
            <select
              className="input"
              value={rackForm.status}
              onChange={(e) => setRackForm({ ...rackForm, status: e.target.value as EntityStatus })}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          {rackModal?.mode === 'create' && (
            <Field label="QR code">
              <label className="flex items-center gap-2 py-2 text-sm text-ink-600">
                <input
                  type="checkbox"
                  checked={rackForm.generateQr}
                  onChange={(e) => setRackForm({ ...rackForm, generateQr: e.target.checked })}
                />
                Generate a QR code now
              </label>
            </Field>
          )}
          <Field label="Description" span>
            <textarea
              className="input min-h-[60px]"
              value={rackForm.description}
              onChange={(e) => setRackForm({ ...rackForm, description: e.target.value })}
            />
          </Field>
        </div>
      </Modal>

      {/* Delete stall */}
      <ConfirmDialog
        open={!!delStall}
        title="Delete stall?"
        tone="danger"
        confirmLabel={delStall?.force ? 'Delete stall + contents' : 'Delete'}
        busy={stallMut.remove.isPending}
        message={
          delStall && (
            <p>
              {delStall.force ? (
                <>
                  <b>{delStall.row.stallNumber}</b> still has racks or product assignments. Deleting now
                  also removes every rack and assignment under it. This cannot be undone.
                </>
              ) : (
                <>
                  Remove <b>{delStall.row.stallNumber}</b>? Stalls tied to bookings or scans can't be
                  deleted — deactivate them instead.
                </>
              )}
            </p>
          )
        }
        onCancel={() => setDelStall(null)}
        onConfirm={confirmDeleteStall}
      />

      {/* Delete rack */}
      <ConfirmDialog
        open={!!delRack}
        title="Delete rack?"
        tone="danger"
        confirmLabel={delRack?.force ? 'Delete rack + assignments' : 'Delete'}
        busy={rackMut.remove.isPending}
        message={
          delRack && (
            <p>
              {delRack.force ? (
                <>
                  <b>{delRack.row.rackNumber}</b> still has product assignments. Deleting now also
                  removes them. This cannot be undone.
                </>
              ) : (
                <>
                  Remove <b>{delRack.row.rackNumber}</b> and its QR code? Racks with bookings, scans or
                  views can't be deleted — deactivate them instead.
                </>
              )}
            </p>
          )
        }
        onCancel={() => setDelRack(null)}
        onConfirm={confirmDeleteRack}
      />

      {/* Regenerate QR */}
      <ConfirmDialog
        open={!!regenRack}
        title="Regenerate QR code?"
        tone="primary"
        confirmLabel="Regenerate"
        busy={rackMut.generateQr.isPending}
        message={
          regenRack && (
            <p>
              A new QR token is issued for <b>{regenRack.rackNumber}</b>. Any printed copies of the old
              code will stop working.
            </p>
          )
        }
        onCancel={() => setRegenRack(null)}
        onConfirm={regenerate}
      />
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</p>
      <p className="mt-0.5 text-xl font-semibold text-ink-900">{value}</p>
    </div>
  );
}

function Field({ label, span, children }: { label: string; span?: boolean; children: React.ReactNode }) {
  return (
    <label className={span ? 'col-span-2 block' : 'block'}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}

/** Drop empty-string optional fields so we don't send blank values the API rejects. */
function trimmed<T extends Record<string, unknown>>(form: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(form)) {
    if (typeof v === 'string') {
      const t = v.trim();
      if (t !== '') out[k] = t;
    } else {
      out[k] = v;
    }
  }
  return out as T;
}
